import { createContext, useCallback, useContext, useEffect, useState } from "react";
import api from "../lib/api";
import { toast } from "sonner";

const CartContext = createContext(null);

export const CartProvider = ({ children }) => {
  const [cart, setCart] = useState({ items: [], count: 0, subtotal: 0, shipping: 0, total: 0 });
  const [loading, setLoading] = useState(true);

  const refresh = useCallback(async () => {
    try {
      const { data } = await api.get("/cart");
      setCart(data);
    } catch (e) {
      // ignore — guest cart endpoint is permissive
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => {
    refresh();
  }, [refresh]);

  const addItem = useCallback(
    async (product_id, sku, qty = 1, productName = "Item") => {
      try {
        const { data } = await api.post("/cart/items", { product_id, sku, qty });
        setCart(data);
        if (data.clamped) {
          toast.warning(`We only have ${data.available_for_sku} left — qty adjusted.`);
        } else {
          toast.success(`${productName} added to your cart.`);
        }
        return data;
      } catch (err) {
        toast.error(err?.response?.data?.detail || "Could not add to cart.");
        throw err;
      }
    },
    []
  );

  const updateQty = useCallback(async (sku, qty) => {
    try {
      const { data } = await api.patch(`/cart/items/${sku}`, { qty });
      setCart(data);
      if (data.clamped) toast.warning("Quantity adjusted to available stock.");
      return data;
    } catch (err) {
      toast.error("Could not update.");
      throw err;
    }
  }, []);

  const removeItem = useCallback(async (sku) => {
    try {
      const { data } = await api.delete(`/cart/items/${sku}`);
      setCart(data);
      toast.success("Removed from cart.");
      return data;
    } catch (err) {
      toast.error("Could not remove.");
      throw err;
    }
  }, []);

  const merge = useCallback(async () => {
    try {
      const { data } = await api.post("/cart/merge");
      setCart(data);
      return data;
    } catch (e) {
      // ignore
    }
  }, []);

  return (
    <CartContext.Provider value={{ cart, loading, refresh, addItem, updateQty, removeItem, merge }}>
      {children}
    </CartContext.Provider>
  );
};

export const useCart = () => {
  const ctx = useContext(CartContext);
  if (!ctx) throw new Error("useCart must be used inside CartProvider");
  return ctx;
};
